import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { rankingList } from '@/mocks/ranking';
import { stats } from '@/mocks/site';

export default function HeroSection() {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState('');
  const topFive = rankingList.slice(0, 5);

  const handleSearch = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = keyword.trim();
    navigate(trimmed ? `/ranking?q=${encodeURIComponent(trimmed)}` : '/ranking');
  };

  return (
    <section className="relative w-full overflow-hidden" aria-labelledby="hero-heading">
      <div className="absolute inset-0 h-full w-full">
        <img
          src="https://readdy.ai/api/search-image?query=Editorial%20abstract%20collage%20depicting%20a%20buzzing%20news%20feed%2C%20floating%20paper%20cards%20and%20trending%20arrows%20over%20a%20warm%20cream%20backdrop%2C%20bold%20orange%20and%20deep%20teal%20shapes%2C%20soft%20grain%20texture%2C%20crisp%20geometric%20composition%2C%20high%20contrast%20graphic%20art%20poster%20style%2C%20minimal%20modern%20illustration&width=1600&height=900&seq=baoliao-hero-01&orientation=landscape"
          alt="抽象信息流与热度箭头的设计背景图"
          className="h-full w-full object-cover object-top"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/80 via-foreground-950/70 to-foreground-950/85" />

      <div className="relative mx-auto w-full max-w-[1280px] px-4 py-14 md:px-6 md:py-20">
        <div className="grid grid-cols-1 items-start gap-10 lg:grid-cols-12 lg:gap-12">
          <div className="w-full lg:col-span-7">
            <span className="inline-flex items-center gap-2 rounded-full border border-background-50/25 bg-background-50/10 px-3 py-1 text-xs text-background-50">
              <span className="inline-flex h-1.5 w-1.5 rounded-full bg-primary-300 animate-softPulse" />
              实时播报中 · 今日已更新 268 条线索
            </span>

            <h1
              id="hero-heading"
              className="mt-5 font-heading text-3xl font-bold leading-tight text-background-50 md:text-4xl lg:text-5xl"
            >
              爆料前线
              <span className="mt-2 block text-xl font-bold text-primary-200 md:text-2xl lg:text-3xl">
                实时热点追踪与突发新闻速递
              </span>
            </h1>

            <p className="mt-5 max-w-2xl text-sm leading-relaxed text-background-50/85 md:text-base">
              刚发生的瓜、正在炒热的帖子、全网正在冲榜的话题，不用开多个 App 来回切。这里把今日鲜料、突发新闻和
              热议排行集中进同一屏，实时盯着事件冲到哪一步。
            </p>

            <form onSubmit={handleSearch} className="mt-7 flex w-full max-w-xl flex-col gap-3 sm:flex-row" role="search">
              <div className="flex flex-1 items-center gap-2 rounded-full border border-background-50/25 bg-background-50/95 px-4 py-3">
                <span className="flex h-4 w-4 items-center justify-center text-foreground-500">
                  <i className="ri-search-line text-base" />
                </span>
                <input
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  type="search"
                  name="keyword"
                  aria-label="搜索热点话题"
                  placeholder="搜索正在发生的话题，例如「音乐节」"
                  className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-500"
                />
              </div>
              <button
                type="submit"
                className="flex shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-fire-line text-base" />
                </span>
                查看热搜榜
              </button>
            </form>

            <div className="mt-6 flex flex-wrap items-center gap-2">
              <span className="text-xs text-background-50/70">热门频道：</span>
              {categories.slice(0, 5).map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="cursor-pointer whitespace-nowrap rounded-full border border-background-50/25 px-3 py-1.5 text-xs text-background-50 transition-colors hover:bg-background-50/15"
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>

          <aside className="w-full lg:col-span-5">
            <div className="rounded-lg border border-background-50/20 bg-background-50/10 p-4 backdrop-blur-sm md:p-5">
              <div className="flex items-center justify-between">
                <h2 className="flex items-center gap-2 font-heading text-base font-bold text-background-50">
                  <span className="flex h-5 w-5 items-center justify-center text-primary-300">
                    <i className="ri-bar-chart-2-line text-lg" />
                  </span>
                  此刻冲榜 TOP 5
                </h2>
                <Link
                  to="/ranking"
                  className="cursor-pointer whitespace-nowrap text-xs text-background-50/75 transition-colors hover:text-background-50"
                >
                  完整榜单
                </Link>
              </div>
              <ol className="mt-4 flex flex-col gap-3">
                {topFive.map((item) => (
                  <li key={item.slug} className="flex items-start gap-3">
                    <span
                      className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-xs font-bold ${
                        item.rank === 1 ? 'bg-primary-500 text-background-50' : 'bg-background-50/20 text-background-50'
                      }`}
                    >
                      {item.rank}
                    </span>
                    <div className="min-w-0">
                      <Link
                        to={`/topic/${item.slug}`}
                        className="line-clamp-2 cursor-pointer text-sm text-background-50 transition-opacity hover:opacity-80"
                      >
                        {item.title}
                      </Link>
                      <span className="mt-1 block text-[11px] text-background-50/60">
                        热度 {(item.heat / 10000).toFixed(1)} 万
                      </span>
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            <dl className="mt-4 grid grid-cols-2 gap-3">
              {stats.map((item) => (
                <div key={item.label} className="rounded-lg border border-background-50/15 bg-background-50/10 px-4 py-3">
                  <dt className="text-xs text-background-50/70">{item.label}</dt>
                  <dd className="mt-1 font-heading text-xl font-bold text-background-50">
                    {item.value}
                    <span className="ml-0.5 text-xs font-normal text-background-50/70">{item.suffix}</span>
                  </dd>
                </div>
              ))}
            </dl>
          </aside>
        </div>
      </div>
    </section>
  );
}